import clsx from 'clsx';

import styles from './Table.module.css';

import type { ReactNode, TdHTMLAttributes, ThHTMLAttributes } from 'react';

export interface TableProps {
  readonly children: ReactNode;
  readonly className?: string;
  /** Accessible name; required when the table has no visible caption. */
  readonly label: string;
}

/** Horizontal overflow is contained here so the page body never scrolls sideways. */
export function Table({ children, className, label }: TableProps) {
  return (
    <div className={styles.scroll}>
      <table className={clsx(styles.table, className)} aria-label={label}>
        {children}
      </table>
    </div>
  );
}

export interface CellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  readonly numeric?: boolean;
  readonly mono?: boolean;
}

export function Td({ numeric, mono, className, children, ...rest }: CellProps) {
  return (
    <td className={clsx(numeric && styles.numeric, mono && styles.mono, className)} {...rest}>
      {children}
    </td>
  );
}

export function Th({
  numeric,
  className,
  children,
  ...rest
}: ThHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return (
    <th scope="col" className={clsx(numeric && styles.numeric, className)} {...rest}>
      {children}
    </th>
  );
}

export interface RowProps {
  readonly children: ReactNode;
  readonly onClick?: () => void;
  readonly className?: string;
}

export function Tr({ children, onClick, className }: RowProps) {
  if (!onClick) return <tr className={className}>{children}</tr>;
  return (
    <tr
      className={clsx(styles.clickable, className)}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      tabIndex={0}
      role="button"
    >
      {children}
    </tr>
  );
}

/** Renders a full-width message row when a table has nothing to show. */
export function EmptyRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className={styles.emptyCell}>
        {children}
      </td>
    </tr>
  );
}

export function RowActions({ children }: { children: ReactNode }) {
  return <div className={styles.actions}>{children}</div>;
}
